<template>
  <div id="root">
    <cover-boost-options-layout
      v-if="coverBoostProducts.length"
      :broker-name="firstProduct.brokerFullName"
      :image="firstProduct.imageSiteRemote"
      :first-name="name"
      :is-same-company="isSameCompany"
      :is-desktop="isDesktop"
      :cover-boost-title-message="firstProduct.coverBoostTitleMessage"
      @close-drawer="onCloseDrawer()"
    >
      <!-- INFORMATION TEXT -->
      <template #main>
        <div class="cover-boost-row mt-5">
          <div class="filler" />
          <div class="information cover-boost-display">
            <p class="chosen-policy-banner">
              <span>{{ chosenPolicyText }}</span>
            </p>
          </div>

          <div class="information cover-boost-banner-display">
            <p class="cover-boost-banner">
              <span>{{ firstProduct.coverBoostOptionsMessage || `Cover Boost ${isDesktop ? 'options': ''}` }}</span>
            </p>
          </div>
        </div>

        <!--COLUMN HEADERS -->

        <div class="cover-boost-row">
          <div class="filler">
            <img
              v-if="isSameCompany"
              :alt="firstProduct.brokerFullName"
              :src="firstProduct.imageSiteRemote"
            >
          </div>
          <div
            v-for="(product, index) in coverBoostProducts"
            :key="`product-${index}`"
            class="column-title cover-boost-display"
          >
            <span
              v-if="isSameCompany"
              :class="`${!isDesktop ? 'fs-14' : 'fs-16'}`"
            >{{ product.productName }}</span>
            <img
              v-else
              :src="product.imageSiteRemote"
              :alt="product.brokerFullName"
            >
          </div>
        </div>

        <!-- MAIN TABLE DATA -->
        <div class="cover-boost-row">
          <div
            class="row-title annual"
          >
            <p
              class="mb-0"
            >
              Annual
            </p>
          </div>
          <div
            v-for="(product, index) in coverBoostProducts"
            :key="`product-${index}`"
            class="cover-boost-display divider cell"
          >
            <span
              v-if="!isDesktop"
              class="fs-14"
            >Annual</span>
            <br v-if="!isDesktop">
            <span class="fs-16">
              &pound;{{ product.calcAnnual.toFixed(2) }}
            </span>
          </div>
        </div>

        <div
          v-if="showMonthly"
          class="cover-boost-row"
        >
          <div
            class="row-title monthly"
          >
            <p
              class="mb-0"
            >
              Monthly
            </p>
          </div>
          <div
            v-for="(product, index) in coverBoostProducts"
            :key="`product-${index}`"
            class="divider cell cover-boost-display"
          >
            <span
              v-if="!isDesktop"
              class="fs-14"
            >Monthly</span>

            <div v-if="product.calcMonthly > 0 && product.calcDeposit > 0">
              <span
                class="fs-14 deposit"
              >1x  &pound;{{ product.calcDeposit.toFixed(2) }}</span>
              <br>
              <span
                :class="`${isDesktop ? 'fs-18' : 'fs-16'}`"
              >
                {{ product.numberOfInstallments }}x
                &pound;{{ product.calcMonthly.toFixed(2) }}
              </span>
              <br>
              <span
                :class="isDesktop ? 'fs-14' : 'fs-12'"
              >
                Total &pound;{{ product.calcMonthlyTotal.toFixed(2) }}
              </span>
            </div>

            <div
              v-else
              class="font-weight-bold"
            >
              Check with provider
            </div>
          </div>
        </div>

        <!--- EXTRAS -->
        <cover-boost-extras
          :cover-boost-products="coverBoostProducts"
          :selected-optional-extras="selectedOptionalExtras"
          :details-description="detailsDescription"
          :is-details-drawer-open="isDetailsDrawerOpen"
          :is-defaqto-rating-shown="isDefaqtoRatingShown"
          :is-desktop="isDesktop"
        />
        <!-- END OF EXTRAS -->

        <!-- DEFAQTO RATING -->
        <div
          v-if="isDefaqtoRatingShown"
          class="cover-boost-row"
        >
          <div class="row-title">
            <v-expansion-panels
              v-model="rowPanelDefaqto"
              flat
            >
              <v-expansion-panel class="pa-0">
                <v-expansion-panel-header>
                  <p class="mb-0">
                    Defaqto Rating
                  </p>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <p class="caption">
                    Defaqto is an independent financial research company.
                    It independently assesses all products in the market, and gives each a rating from 1 to 5 based on the quality and comprehensiveness
                    of the features and benefits it offers.
                  </p>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </div>

          <div
            v-for="(product, index) in coverBoostProducts"
            :key="`product-rating-${index}`"
            class="cell cover-boost-display last"
          >
            <p
              v-if="!isDesktop"
              class="fs-14"
            >
              Defaqto Rating
            </p>

            <defaqto-sticker
              v-if="product.defaqtoRating"
              :rating="Number(product.defaqtoRating)"
              class="mb-5 defaqto"
            />
            <span v-else>Please check with provider</span>
          </div>
        </div>

        <!-- END OF DEFAQTO -->

        <!-- SELECT BUTTONS -->
        <div
          class="cover-boost-row mt-6"
        >
          <div class="filler" />
          <div
            v-for="(product, index) in coverBoostProducts"
            :key="`product-select-${index}`"
            class="cover-boost-display buttons"
          >
            <v-btn
              block
              filled
              rounded
              outlined
              selected
              color="primary"
              @click="onSelect(product)"
            >
              <span class="font-weight-medium">Select</span>
            </v-btn>
          </div>
        </div>

        <!-- PROMOTION BOX ROW -->
        <div class="cover-boost-row">
          <div class="filler" />
          <div
            v-for="(product, index) in coverBoostProducts"
            :key="`promotion-${index}`"
            class="cover-boost-display promotion-box"
          >
            <div v-if="index === (coverBoostProducts.length - 1)">
              <div class="arrow" />
              <div
                class="box"
                v-html="coverBoostMessage"
              />
            </div>
          </div>
        </div>
      </template>
    </cover-boost-options-layout>
  </div>
</template>

<script>
import CoverBoostOptionsLayout from '@/components/CoverBoost/CoverBoostOptionsLayout';
import CoverBoostExtras from '@/components/CoverBoost/CoverBoostExtras';
import DefaqtoSticker from '@/components/Rating/DefaqtoSticker';
import { compactNumber } from '@/helpers/common';
import { useCoverBoostTrackers } from '@/composables/coverBoostTrackers';
import { inject } from '@vue/composition-api';

export default {
  name: 'cover-boost-options',
  components: {
    CoverBoostOptionsLayout,
    CoverBoostExtras,
    DefaqtoSticker,
  },
  inject: [
    'productName',
    'constants',
    'helpers',
  ],
  props: {
    showMonthly: {
      type: Boolean,
      default: true,
    },
    name: {
      type: String,
      default: undefined,
    },
    isDetailsDrawerOpen: {
      type: Boolean,
      default: false,
    },
    coverBoostProducts: {
      type: Array,
      default: undefined,
    },
    selectedOptionalExtras: {
      type: Array,
      required: true,
    },
    quote: {
      required: false,
      type: Object,
      default: undefined,
    },
    detailsDescription: {
      type: Object,
      required: true,
    },
    isDesktop: {
      type: Boolean,
      required: true,
    },
  },
  mounted () {
    this.setProductLength(this.coverBoostProducts.length);
  },
  data () {
    return {
      rowPanelDefaqto: [],
    };
  },
  watch: {
    coverBoostProducts (val) {
      this.setProductLength(val.length);
    },
  },
  computed: {
    chosenPolicyText () {
      if (this.isDesktop) {
        return 'Your chosen policy';
      }

      // 355px is the width at which the words overlap on smaller screens
      // On sizes less than or equal to this we need to amend the text
      return this.$vuetify.breakpoint.width <= 355 ? 'Chosen' : 'Chosen policy';
    },
    firstProduct () {
      return this.coverBoostProducts[0];
    },
    isSameCompany () {
      return this.firstProduct.boostType === 'upsell';
    },
    coverBoostMessage () {
      const length = this.coverBoostProducts.length;
      const difference = (this.coverBoostProducts[length - 1]?.calcAnnual - this.firstProduct.calcAnnual).toFixed(2);

      const productWithMessage = this.coverBoostProducts.find(product => {
        return product.coverBoostMessage;
      });

      return productWithMessage.coverBoostMessage.replace('@VALUE', `&pound${difference}`);
    },
  },
  methods: {
    setProductLength (val) {
      document.getElementById('root').style.setProperty('--cover-boost-products', val);
    },
    onSelect (detail) {
      this.setCoverBoostClick(this.coverBoostProducts.indexOf(detail));
      this.$emit('click-view-details', detail.premiumID);
      this.$emit('open-drawer-details', ['results-details', 'optionalExtras']);
    },
    onCloseDrawer () {
      this.rowPanelDefaqto = [];
      this.$emit('close-drawer');
      this.setClosed();
    },
  },
  setup (props, context) {
    const { isDefaqtoRatingShown } = inject('abTastyControllerUI');
    const { setCoverBoostClick, setClosed } = useCoverBoostTrackers(context);
    return {
      isDefaqtoRatingShown,
      compactNumber,
      setCoverBoostClick,
      setClosed,
    };
  },
};
</script>

<style lang="scss">
$cover-boost-first-column-width: calc(100% / (var(--cover-boost-products) + 1));
$cover-boost-green: #41a403;
$cover-boost-gray: #d9d9d9;
$cover-boost-blue: #0e80e8;
$cover-boost-divider: #73d7f6;
$cover-boost-yellow: #fff9a0;

$column-width-desktop: calc((100% - #{$cover-boost-first-column-width}) / var(--cover-boost-products));
$column-width-mobile: calc(100% / var(--cover-boost-products));

#root {
  --cover-boost-products: 3;
}

.cover-boost-row {
  width: 100%;
  display: table;
  border-spacing: 5px 0;

  @media screen and (min-width: 768px) {
    border-spacing: 10px 0;
  }

  .v-expansion-panel--active > .v-expansion-panel-header {
    min-height: auto;
  }

  div.last {
    border-bottom: 2px solid $cover-boost-green;
  }

  .filler {
    width: $cover-boost-first-column-width;
    display: none;

    @media screen and (min-width: 768px) {
      display: table-cell;
    }
  }

  & > div.column-title:last-of-type {
    background-color: $cover-boost-green;
    color: white;
    border-width: 4px;
  }

  & > div.column-title:nth-child(2) {
    border-color: $cover-boost-gray;
    background-color: rgba($cover-boost-gray, 0.2);
  }

  & > div:not(:first-child) {
    border-left-width: 2px;
    border-right-width: 2px;
    border-left-color: $cover-boost-green;
    border-right-color: $cover-boost-green;
    border-style: solid;
  }

  & > div:nth-child(2) {
    border-left-color: $cover-boost-gray;
    border-right-color: $cover-boost-gray;
    background-color: rgba($cover-boost-gray, 0.2);
    margin-right: 1em;

    & > .icon {
      @media screen and (max-width: 768px) {
        margin-right: 1em;
      }
    }

    &.last {
      border-bottom-color: $cover-boost-gray;
    }
  }

  & > div:last-of-type {
    border-left-width: 4px;
    border-right-width: 4px;

    &.last {
      border-bottom-width: 4px;
    }
  }

  & > div:first-of-type {
    border-width: 1px;
  }

  & > div:not(.cell):not(.column-title):not(:first-of-type) {
    background-color: transparent;
    border-width: 0;
  }

  .cover-boost-display {
    display: table-cell;
    width: $column-width-mobile;

    @media screen and (min-width: 768px) {
      width: $column-width-desktop;
    }
  }

  .row-title {
    border-bottom: 1px solid rgba(165, 165, 165, 1);
    width: $cover-boost-first-column-width;
    display: none;

    @media screen and (min-width: 768px) {
      display: table-cell;
    }
  }

  .column-title {
    text-align: center;
    vertical-align: middle;
    border-top-left-radius: 5px;
    border-top-right-radius: 5px;
    font-weight: bold;
    border: 2px solid $cover-boost-green;
    background-color: rgba($cover-boost-green, 0.2);

    img {
      max-width: 100%;
      max-height: 100%;
    }
  }

  .arrow {
    width: 15px;
    border-left: 20px solid transparent;
    border-right: 20px solid transparent;
    border-bottom: 15px solid $cover-boost-yellow;
    margin: 0 auto;
  }

  .box {
    background: $cover-boost-yellow;
    padding: 10px;
    text-align: center;
  }

  .cell {
    text-align: center;
    vertical-align: top;
    border-top: 0;
    margin: 2px 0;
  }

  .divider {
    border-bottom: 1px solid $cover-boost-divider;
  }

  .annual {
    line-height: 2;
  }

  .monthly {
    line-height: 3;
    vertical-align: middle;
  }

  .break {
    word-break: break-word;
  }

  .deposit {
    color: $cover-boost-blue;
  }

  .defaqto {
    margin: auto;
    width: 40%;

    @media screen and (min-width: 768px) {
      width: 80%;
    }
  }

  .information {
    text-align: center;
    vertical-align: middle;

    .chosen-policy-banner {
      color: $cover-boost-blue;
      line-height: 0.1em;
      border-bottom: 0 solid $cover-boost-blue;

      @media screen and (min-width: 768px) {
        border-bottom-width: 2px;
      }
    }

    .cover-boost-banner {
      border-bottom: 2px solid $cover-boost-green;
      line-height: 0.1em;
      color: $cover-boost-green;
    }

    span {
      background: #fff;
      font-weight: bold;
      padding-right: 2px;
      padding-left: 2px;
    }
  }
}

.sub-excess-cost {
  color: #555;
}
</style>
